import api from './api';
import { ODPListResponse, ODPStatsResponse, ODP, VLANInterface, RouterInterface } from '../types';

const odpService = {
  // Get all ODPs with pagination and search
  getODPs: async (page = 1, limit = 10, search = '', isActive?: boolean, spopId?: string, odcId?: string): Promise<ODPListResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      search
    });

    if (isActive !== undefined) {
      params.append('isActive', isActive.toString());
    }

    if (spopId) {
      params.append('spopId', spopId);
    }

    if (odcId) {
      params.append('odcId', odcId);
    }

    const response = await api.get(`/odps?${params.toString()}`);
    return response.data;
  },

  // Get ODP statistics
  getODPStats: async (filters?: Record<string, any>): Promise<ODPStatsResponse> => {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== null) {
          params.append(key, filters[key].toString());
        }
      });
    }
    
    const queryString = params.toString();
    const response = await api.get(`/odps/stats${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },

  // Get ODP by ID
  getODPById: async (id: string): Promise<{ success: boolean; data: ODP }> => {
    const response = await api.get(`/odps/${id}`);
    return response.data;
  },

  // Create new ODP with photo upload
  createODP: async (odpData: any, photo?: File): Promise<{ success: boolean; data: ODP; message: string }> => {
    const formData = new FormData();
    
    // Add all ODP data to form data
    Object.keys(odpData).forEach(key => {
      if (odpData[key] !== undefined && odpData[key] !== null) {
        formData.append(key, odpData[key].toString());
      }
    });

    // Add photo if provided
    if (photo) {
      formData.append('photo', photo);
    }

    const response = await api.post('/odps', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Update ODP with optional photo upload
  updateODP: async (id: string, odpData: any, photo?: File): Promise<{ success: boolean; data: ODP; message: string }> => {
    const formData = new FormData();
    
    // Add all ODP data to form data
    Object.keys(odpData).forEach(key => {
      if (odpData[key] !== undefined && odpData[key] !== null) {
        formData.append(key, odpData[key].toString());
      }
    });

    // Add photo if provided
    if (photo) {
      formData.append('photo', photo);
    }

    const response = await api.put(`/odps/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Delete ODP
  deleteODP: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/odps/${id}`);
    return response.data;
  },

  // Upload photo
  uploadPhoto: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('photo', file);
    
    const response = await api.post('/odps/upload-photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data.filename;
  },

  // Get router VLANs
  getRouterVLANs: async (routerId: string): Promise<{ success: boolean; data: VLANInterface[] }> => {
    const response = await api.get(`/routers/${routerId}/vlans`);
    return response.data;
  },

  // Get router interfaces
  getRouterInterfaces: async (routerId: string): Promise<{ success: boolean; data: RouterInterface[] }> => {
    const response = await api.get(`/routers/${routerId}/interfaces`);
    return response.data;
  },

  // Get photo URL
  getPhotoUrl: (filename: string): string => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3000';
    return `${baseUrl}/uploads/odp/${filename}`;
  }
};

export default odpService;
